<template>
  <section class="manifest-detail-page">
    <div v-if="isLoading" class="manifest-loading-state text-center py-5">
      <b-spinner variant="primary" class="mb-1" />
      <p class="text-muted mb-0">Chargement du bon de sous-traitance...</p>
    </div>

    <b-alert v-else-if="!manifest" variant="danger" show>
      <h4 class="alert-heading">Bon introuvable</h4>
      <div class="alert-body mb-0">
        Impossible de charger le détail de ce bon de sous-traitance.
      </div>
    </b-alert>

    <div v-else>
      <div class="manifest-screen-content">
      <div class="manifest-page-header mb-3">
        <div>
          <div class="manifest-eyebrow">Suivi sous-traitance</div>
          <div class="d-flex flex-wrap align-items-center gap-1 mb-50">
            <h2 class="manifest-title mb-0">Bon de sous-traitance #{{ manifest.id }}</h2>
            <b-badge variant="light-primary" pill class="manifest-pill">
              {{ manifestOrders.length }} colis
            </b-badge>
            <b-badge :variant="statusVariant(manifest.status)" pill class="manifest-pill">
              {{ statusLabel(manifest.status) }}
            </b-badge>
          </div>
          <p class="text-muted mb-0">
            Créé le {{ formatDate(manifest.created_at) }}
          </p>
        </div>

        <div class="manifest-header-actions">
          <b-button variant="outline-primary" @click="printManifest">
            <feather-icon icon="PrinterIcon" class="mr-50" />
            Imprimer
          </b-button>
          <b-button variant="outline-secondary" :to="{ name: 'manifests-list' }">
            <feather-icon icon="ArrowLeftIcon" class="mr-50" />
            Retour à la liste
          </b-button>
        </div>
      </div>

      <b-row class="mb-2">
        <b-col md="4" sm="6" class="mb-1">
          <div class="manifest-stat-card stat-company h-100">
            <div class="manifest-stat-icon">
              <feather-icon icon="BriefcaseIcon" size="18" />
            </div>
            <div>
              <div class="manifest-stat-label">Société de sous-traitance</div>
              <div class="manifest-stat-value">{{ outsourcingCompanyName }}</div>
            </div>
          </div>
        </b-col>

        <b-col md="4" sm="6" class="mb-1">
          <div class="manifest-stat-card stat-user h-100">
            <div class="manifest-stat-icon">
              <feather-icon icon="UserIcon" size="18" />
            </div>
            <div>
              <div class="manifest-stat-label">Créé par</div>
              <div class="manifest-stat-value">{{ createdByLabel }}</div>
              <small class="text-muted">{{ createdByRole }}</small>
            </div>
          </div>
        </b-col>

        <b-col md="4" sm="6" class="mb-1">
          <div class="manifest-stat-card stat-cod h-100">
            <div class="manifest-stat-icon">
              <feather-icon icon="DollarSignIcon" size="18" />
            </div>
            <div>
              <div class="manifest-stat-label manifest-cod-label">Montant contre remboursement</div>
              <div class="manifest-stat-value manifest-cod-total">{{ formatAmount(cashOnDeliveryTotal) }}</div>
              <small class="text-muted">Somme des prix TTC</small>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-row class="manifest-summary-row mb-3">
        <b-col xl="3" md="6" class="mb-1">
          <div class="manifest-summary-card summary-to-collect h-100">
            <div class="manifest-summary-label">À encaisser société</div>
            <div class="manifest-summary-value">{{ formatAmount(toCollectAmountTotal) }}</div>
            <small>{{ deliveredUnpaidOrdersCount }} colis livré non payé<span v-if="deliveredUnpaidOrdersCount > 1">s</span></small>
          </div>
        </b-col>
        <b-col xl="3" md="6" class="mb-1">
          <div class="manifest-summary-card summary-collected h-100">
            <div class="manifest-summary-label">Déjà encaissé</div>
            <div class="manifest-summary-value">{{ formatAmount(collectedAmountTotal) }}</div>
            <small>{{ paidDeliveredOrdersCount }} colis livré payé<span v-if="paidDeliveredOrdersCount > 1">s</span></small>
          </div>
        </b-col>
        <b-col xl="3" md="6" class="mb-1">
          <div class="manifest-summary-card summary-return-pending h-100">
            <div class="manifest-summary-label">Retours chez société</div>
            <div class="manifest-summary-value">{{ depotReturnOrdersCount }}</div>
            <small>Retour au dépôt non clôturé</small>
          </div>
        </b-col>
        <b-col xl="3" md="6" class="mb-1">
          <div class="manifest-summary-card summary-return-final h-100">
            <div class="manifest-summary-label">Retours finalisés</div>
            <div class="manifest-summary-value">{{ finalizedReturnOrdersCount }}</div>
            <small>Retour fournisseur / payé / reçu</small>
          </div>
        </b-col>
        <b-col lg="4" class="mb-1">
          <div class="manifest-summary-card summary-progress summary-featured h-100">
            <div class="manifest-summary-label">Progression du bon</div>
            <div class="manifest-summary-value">{{ treatmentProgressLabel }}</div>
            <div class="manifest-progress-track mt-50">
              <div class="manifest-progress-fill" :style="{ width: `${treatmentProgressPercent}%` }" />
            </div>
            <small>{{ treatmentProgressPercent }}% traité</small>
          </div>
        </b-col>
        <b-col lg="8" class="mb-1">
          <div class="manifest-summary-card summary-breakdown summary-featured h-100">
            <div class="section-title-row mb-50">
              <div>
                <div class="manifest-summary-label">Répartition par statut</div>
              </div>
              <small class="text-muted">{{ manifestOrders.length }} colis</small>
            </div>
            <div v-if="statusBreakdown.length" class="manifest-status-breakdown">
              <b-badge
                v-for="statusItem in statusBreakdown"
                :key="statusItem.key"
                :variant="orderStatusVariant(statusItem.status)"
                pill
                class="manifest-status-chip"
              >
                {{ statusItem.label }}: {{ statusItem.count }}
              </b-badge>
            </div>
            <small v-else>Aucun statut disponible</small>
          </div>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col lg="8" class="mb-2 mb-lg-0">
          <b-card class="manifest-section-card h-100" no-body>
            <b-card-body>
              <div class="section-title-row mb-1">
                <div>
                  <h5 class="mb-25">Informations générales</h5>
                  <p class="text-muted mb-0">Vue rapide du bon et de son affectation.</p>
                </div>
              </div>

              <div class="manifest-meta-grid">
                <div class="manifest-meta-item">
                  <span class="manifest-meta-label">Référence</span>
                  <span class="manifest-meta-value">#{{ manifest.id }}</span>
                </div>
                <div class="manifest-meta-item">
                  <span class="manifest-meta-label">Chauffeur</span>
                  <span class="manifest-meta-value">{{ driverLabel }}</span>
                </div>
                <div class="manifest-meta-item">
                  <span class="manifest-meta-label">Véhicule</span>
                  <span class="manifest-meta-value">{{ manifest.car || '-' }}</span>
                </div>
                <div class="manifest-meta-item">
                  <span class="manifest-meta-label">Dernière mise à jour</span>
                  <span class="manifest-meta-value">{{ formatDate(manifest.updated_at) }}</span>
                </div>
                <div v-if="manifest.status === 'annule'" class="manifest-meta-item">
                  <span class="manifest-meta-label">Annulé par</span>
                  <span class="manifest-meta-value">{{ cancelledByLabel }}</span>
                </div>
                <div v-if="manifest.status === 'annule'" class="manifest-meta-item">
                  <span class="manifest-meta-label">Annulé le</span>
                  <span class="manifest-meta-value">{{ formatDate(manifest.cancelled_at) }}</span>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <b-col lg="4">
          <b-card class="manifest-section-card h-100" no-body>
            <b-card-body>
              <h5 class="mb-25">Note</h5>
              <p v-if="manifest.note" class="manifest-note mb-0">
                {{ manifest.note }}
              </p>
              <p v-else-if="manifest.cancel_reason" class="manifest-note mb-0">
                {{ manifest.cancel_reason }}
              </p>
              <p v-else class="text-muted mb-0">
                Aucune note ajoutée pour ce bon.
              </p>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-card class="manifest-section-card" no-body>
        <b-card-body class="pb-0">
          <div class="section-title-row mb-2">
            <div>
              <h5 class="mb-25">Colis dans ce bon</h5>
              <p class="text-muted mb-0">Consultez, recherchez et gérez les colis déjà affectés.</p>
            </div>
            <div class="manifest-table-tools">
              <b-form-select
                v-model="statusFilter"
                :options="statusFilterOptions"
                size="sm"
                class="manifest-status-filter"
              />
              <b-form-select
                v-model="treatmentFilter"
                :options="treatmentFilterOptions"
                size="sm"
                class="manifest-treatment-filter"
              />
              <b-badge variant="light-primary" pill>
                {{ filteredManifestOrders.length }} ligne<span v-if="filteredManifestOrders.length > 1">s</span>
              </b-badge>
            </div>
          </div>
        </b-card-body>

        <div class="px-1 pb-1">
          <div v-if="manifestOrders.length" class="manifest-table-shell">
            <vue-good-table
              :columns="orderColumns"
              :rows="filteredManifestOrders"
              :row-style-class="rowStyleClass"
              :search-options="{ enabled: true, placeholder: 'Rechercher un colis dans ce bon' }"
              :pagination-options="{ enabled: true, perPage: 10 }"
            >
              <template slot="table-row" slot-scope="props">
                <span v-if="props.column.field === 'added_by'">
                  <span class="font-weight-bolder d-block">{{ props.row.added_by && props.row.added_by.username }}</span>
                  <small class="text-muted">{{ formatDate(props.row.added_at) }}</small>
                </span>
                <span v-else-if="props.column.field === 'order_price_ttc'">
                  <span class="font-weight-bolder">{{ formatAmount(props.row.order_price_ttc) }}</span>
                </span>
                <span v-else-if="props.column.field === 'order_status_display'">
                  <b-badge :variant="orderStatusVariant(props.row.order_status)" pill>
                    {{ props.row.order_status_display || getOrderStatusLabel(props.row.order_status) }}
                  </b-badge>
                  <small v-if="props.row.order_status_changed_at" class="text-muted d-block mt-25">
                    {{ formatDate(props.row.order_status_changed_at) }}
                  </small>
                </span>
                <span v-else-if="props.column.field === 'treatment'">
                  <b-badge :variant="props.row.treated_at ? 'light-success' : 'light-warning'" pill>
                    {{ props.row.treated_at ? 'Clôturé' : 'À clôturer' }}
                  </b-badge>
                  <small v-if="props.row.treated_at" class="text-muted d-block">
                    {{ props.row.treated_status_display || props.row.treated_status }} - {{ formatDate(props.row.treated_at) }}
                  </small>
                </span>
                <span v-else>{{ props.formattedRow[props.column.field] || '-' }}</span>
              </template>
            </vue-good-table>
          </div>

          <div v-else class="manifest-empty-state manifest-empty-state-large">
            <feather-icon icon="PackageIcon" size="24" class="mb-50 text-muted" />
            <h6 class="mb-50">Aucun colis dans ce bon</h6>
            <p class="text-muted mb-0">
              Ce bon ne contient encore aucun colis.
            </p>
          </div>
        </div>
      </b-card>
      </div>

      <section class="manifest-print-wrapper" aria-label="Impression bon de sous-traitance pickup">
        <div class="manifest-print-logo">
          <img src="/logo.png" alt="Logo de la société">
        </div>

        <div class="manifest-print-title-row">
          <h1>Bon de sous-traitance pickup N° : {{ manifest.id }}</h1>
          <h2>Date : {{ formatPrintDate(manifest.created_at) }}</h2>
        </div>

        <div class="manifest-print-info-row">
          <div class="manifest-print-info-card">
            <div class="manifest-print-info-line">
              <strong>{{ deliveryCompany.name || '-' }}</strong> : {{ deliveryCompany.tagline || 'Société de livraison des colis' }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Matricule fiscale :</strong> {{ deliveryCompany.tax_identification_number || '-' }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Adresse :</strong> {{ deliveryCompany.address || '-' }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Tel :</strong> {{ deliveryCompany.phone1 || deliveryCompany.phone || '-' }}
            </div>
          </div>

          <div class="manifest-print-info-card">
            <div class="manifest-print-info-line">
              <strong>Société sous-traitance :</strong> {{ outsourcingCompanyName }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Matricule fiscale :</strong> {{ outsourcingCompanyTaxId }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Adresse :</strong> {{ outsourcingCompanyAddress }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Tel :</strong> {{ outsourcingCompanyPhone }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Nombre des colis :</strong> {{ manifestOrders.length }}
            </div>
          </div>
        </div>

        <table class="manifest-print-table">
          <thead>
            <tr>
              <th>BL</th>
              <th>Destinataire</th>
              <th>Adresse</th>
              <th>Prix</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in manifestOrders" :key="order.id">
              <td>{{ order.order_id }}</td>
              <td>
                <strong>{{ order.order_name || '-' }}</strong>
              </td>
              <td>
                <strong>{{ order.order_governorate || '-' }}</strong>
                <span>{{ order.order_address || '-' }}</span>
              </td>
              <td>{{ formatAmount(order.order_price_ttc) }}</td>
            </tr>
          </tbody>
        </table>

        <div class="manifest-print-footer">
          <h3>Signature</h3>
          <h2>Total : {{ formatAmount(cashOnDeliveryTotal) }}</h2>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { VueGoodTable } from 'vue-good-table'
import storeAuth from '@/store/store'
import { getOrderStatusLabel } from '@/core/utils/orderStatus'

export default {
  name: 'ManifestShow',
  components: { VueGoodTable },
  props: {
    id: { type: [String, Number], required: true },
  },
  data() {
    return {
      refreshTimer: null,
      statusFilter: null,
      treatmentFilter: null,
    }
  },
  computed: {
    ...mapGetters('manifestsPickup', ['manifest', 'isLoading']),
    ...mapGetters(['userRole']),
    manifestOrders() {
      return this.manifest?.manifest_orders || []
    },
    deliveryCompany() {
      return storeAuth.state.company || {}
    },
    cashOnDeliveryTotal() {
      return this.sumOrderAmounts(this.manifestOrders)
    },
    deliveredUnpaidOrders() {
      return this.manifestOrders.filter(order => Number(order.order_status) === 4)
    },
    paidDeliveredOrders() {
      return this.manifestOrders.filter(order => Number(order.order_status) === 6)
    },
    finalizedReturnOrders() {
      return this.manifestOrders.filter(order => [7, 9, 28].includes(Number(order.order_status)))
    },
    deliveredUnpaidOrdersCount() {
      return this.deliveredUnpaidOrders.length
    },
    paidDeliveredOrdersCount() {
      return this.paidDeliveredOrders.length
    },
    depotReturnOrdersCount() {
      return this.manifestOrders.filter(order => Number(order.order_status) === 5).length
    },
    finalizedReturnOrdersCount() {
      return this.finalizedReturnOrders.length
    },
    toCollectAmountTotal() {
      return this.sumOrderAmounts(this.deliveredUnpaidOrders)
    },
    collectedAmountTotal() {
      return this.sumOrderAmounts(this.paidDeliveredOrders)
    },
    treatmentProgressLabel() {
      const treatedCount = this.manifest?.treated_count || 0
      const ordersCount = this.manifest?.orders_count || this.manifestOrders.length
      return `${treatedCount} / ${ordersCount}`
    },
    treatmentProgressPercent() {
      const treatedCount = this.manifest?.treated_count || 0
      const ordersCount = this.manifest?.orders_count || this.manifestOrders.length
      if (!ordersCount) return 0
      return Math.round((treatedCount / ordersCount) * 100)
    },
    statusBreakdown() {
      const statusMap = this.manifestOrders.reduce((acc, order) => {
        const status = Number(order.order_status)
        const label = order.order_status_display || getOrderStatusLabel(status)
        const key = `${status}-${label}`

        if (!acc[key]) {
          acc[key] = {
            key,
            status,
            label,
            count: 0,
          }
        }
        acc[key].count += 1
        return acc
      }, {})

      return Object.values(statusMap).sort((first, second) => {
        if (second.count !== first.count) return second.count - first.count
        return first.label.localeCompare(second.label)
      })
    },
    filteredManifestOrders() {
      let rows = this.manifestOrders

      if (this.statusFilter !== null && this.statusFilter !== undefined && this.statusFilter !== '') {
        rows = rows.filter(row => Number(row.order_status) === Number(this.statusFilter))
      }

      if (this.treatmentFilter === 'closed') {
        rows = rows.filter(row => row.treated_at)
      } else if (this.treatmentFilter === 'open') {
        rows = rows.filter(row => !row.treated_at)
      }

      return rows
    },
    statusFilterOptions() {
      const seen = new Set()
      const options = [
        { value: null, text: 'Tous les statuts' },
      ]

      this.manifestOrders.forEach(row => {
        const statusValue = Number(row.order_status)
        if (seen.has(statusValue)) return
        seen.add(statusValue)
        options.push({
          value: statusValue,
          text: row.order_status_display || getOrderStatusLabel(statusValue),
        })
      })

      return options
    },
    treatmentFilterOptions() {
      return [
        { value: null, text: 'Tous les traitements' },
        { value: 'open', text: 'À clôturer' },
        { value: 'closed', text: 'Clôturé' },
      ]
    },
    outsourcingCompanyName() {
      return this.manifest?.outsourcing_company?.display_name || this.manifest?.outsourcing_company?.name || '-'
    },
    outsourcingCompanyAddress() {
      return this.manifest?.outsourcing_company?.address || '-'
    },
    outsourcingCompanyPhone() {
      return this.manifest?.outsourcing_company?.phone || this.manifest?.outsourcing_company?.phone2 || '-'
    },
    outsourcingCompanyTaxId() {
      return this.manifest?.outsourcing_company?.tax_identification_number || '-'
    },
    createdByLabel() {
      return this.manifest?.created_by?.username || '-'
    },
    createdByRole() {
      return this.manifest?.created_by?.role || '-'
    },
    driverLabel() {
      return this.manifest?.driver?.username || '-'
    },
    cancelledByLabel() {
      return this.manifest && this.manifest.cancelled_by ? this.manifest.cancelled_by.username : '-'
    },
    orderColumns() {
      return [
        { label: 'Référence', field: 'order_id' },
        { label: 'Client', field: 'order_name' },
        { label: 'Téléphone', field: 'order_phone1' },
        { label: 'Gouvernorat', field: 'order_governorate' },
        { label: 'Adresse', field: 'order_address' },
        { label: 'Prix TTC', field: 'order_price_ttc', type: 'number' },
        { label: 'État actuel', field: 'order_status_display', sortable: false },
        { label: 'Traitement', field: 'treatment', sortable: false },
        { label: 'Ajouté par / le', field: 'added_by' },
      ]
    },
  },
  created() {
    this.fetchManifest(this.id)
    this.refreshTimer = window.setInterval(() => {
      this.fetchManifestOrderStatuses(this.id)
    }, 10000)
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      window.clearInterval(this.refreshTimer)
    }
  },
  methods: {
    ...mapActions('manifestsPickup', ['fetchManifest', 'fetchManifestOrderStatuses']),
    getOrderStatusLabel,
    sumOrderAmounts(orders) {
      return orders.reduce((sum, order) => {
        const amount = Number.parseFloat(order.order_price_ttc)
        return Number.isNaN(amount) ? sum : sum + amount
      }, 0)
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleString('fr-TN')
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === '') return '-'
      const amount = Number.parseFloat(value)
      if (Number.isNaN(amount)) return `${value} DT`
      return `${amount.toFixed(3)} DT`
    },
    formatPrintDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      if (Number.isNaN(date.getTime())) return dateStr
      return date.toISOString().slice(0, 10)
    },
    printManifest() {
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => { window.print() })
      } else {
        window.print()
      }
    },
    statusVariant(statusValue) {
      const variants = {
        ouvert: 'light-warning',
        en_cours: 'light-warning',
        termine: 'light-success',
        annule: 'light-danger',
      }
      return variants[statusValue] || 'light-secondary'
    },
    statusLabel(statusValue) {
      if (statusValue === 'termine') return 'Clôturé'
      if (statusValue === 'annule') return 'Annulé'
      return 'Non clôturé'
    },
    orderStatusVariant(statusValue) {
      if (Number(statusValue) === 4) return 'light-success'
      if ([5, 22].includes(Number(statusValue))) return 'light-danger'
      return 'light-secondary'
    },
    rowStyleClass(row) {
      const status = Number(row.order_status)
      if (status === 4) return 'manifest-row-delivered'
      if ([5, 22].includes(status)) return 'manifest-row-returned'
      return ''
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>

<style scoped>
.manifest-detail-page {
  padding-bottom: 1rem;
}

.manifest-page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-radius: 1rem;
  background: linear-gradient(135deg, #fffaf2 0%, #ffffff 55%, #f3f7ff 100%);
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
}

.manifest-eyebrow {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a6d3b;
  margin-bottom: 0.35rem;
}

.manifest-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #223047;
}

.manifest-pill {
  font-size: 0.8rem;
}

.manifest-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.manifest-print-wrapper {
  display: none;
}

.manifest-stat-card {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  padding: 1rem 1.1rem;
  border-radius: 0.9rem;
  border: 1px solid rgba(226, 232, 240, 0.95);
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.manifest-stat-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.75);
}

.stat-company .manifest-stat-icon {
  color: #c27c2c;
  background: #fff3df;
}

.stat-user .manifest-stat-icon {
  color: #0f766e;
  background: #ddfbf5;
}

.stat-cod {
  border-color: #f4b266;
  background: #fff7ed;
  box-shadow: 0 10px 28px rgba(143, 63, 0, 0.12);
}

.stat-cod .manifest-stat-icon {
  color: #8f3f00;
  background: #ffd9a8;
}

.manifest-cod-label {
  font-size: 0.86rem;
  font-weight: 800;
  color: #7a4b19;
}

.manifest-cod-total {
  font-size: 1.9rem;
  line-height: 1.15;
  font-weight: 900;
  color: #8f3f00;
}

.manifest-stat-label,
.manifest-meta-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #7b8798;
  margin-bottom: 0.2rem;
}

.manifest-stat-value,
.manifest-meta-value {
  display: block;
  font-size: 1.35rem;
  line-height: 1.15;
  font-weight: 800;
  color: #223047;
}

.manifest-section-card {
  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: 1rem;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
}

.manifest-summary-row {
  align-items: stretch;
}

.manifest-summary-card {
  padding: 1rem 1.1rem;
  border-radius: 0.9rem;
  border: 1px solid rgba(226, 232, 240, 0.95);
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}

.summary-to-collect {
  border-color: #f4b266;
  background: #fff7ed;
}

.summary-collected {
  border-color: #b7e5d2;
  background: #f1fbf6;
}

.summary-return-pending {
  border-color: #f1d79d;
  background: #fffbeb;
}

.summary-return-final {
  border-color: #c9d8ff;
  background: #f6f8ff;
}

.summary-progress {
  border-color: #c9d8ff;
  background: #f6f8ff;
}

.summary-breakdown {
  border-color: #d2d9e8;
  background: #ffffff;
}

.summary-featured {
  box-shadow: 0 12px 30px rgba(54, 87, 214, 0.11);
}

.summary-progress .manifest-summary-label,
.summary-breakdown .manifest-summary-label {
  color: #4b5f9f;
}

.summary-progress .manifest-summary-value {
  font-size: 1.75rem;
}

.manifest-summary-label {
  font-size: 0.78rem;
  font-weight: 800;
  color: #687386;
  margin-bottom: 0.35rem;
}

.manifest-summary-value {
  font-size: 1.35rem;
  line-height: 1.15;
  font-weight: 900;
  color: #223047;
}

.manifest-progress-track {
  width: 100%;
  height: 0.65rem;
  overflow: hidden;
  border-radius: 999px;
  background: #e2e8f0;
}

.manifest-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: #3657d6;
  transition: width 0.2s ease;
}

.manifest-status-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.manifest-status-chip {
  font-size: 0.86rem;
  padding: 0.35rem 0.65rem;
}

.section-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.manifest-table-tools {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.manifest-status-filter {
  width: 190px;
  min-width: 170px;
}

.manifest-treatment-filter {
  width: 175px;
  min-width: 155px;
}

.manifest-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.manifest-meta-item {
  padding: 0.9rem 1rem;
  border-radius: 0.85rem;
  background: #f8fafc;
  border: 1px solid #edf2f7;
}

.manifest-note {
  line-height: 1.7;
  color: #344054;
}

.manifest-table-shell {
  border-radius: 0.9rem;
  overflow: hidden;
}

.manifest-table-shell ::v-deep tr.manifest-row-delivered td {
  background-color: #e7f7f1 !important;
}

.manifest-table-shell ::v-deep tr.manifest-row-delivered:hover td {
  background-color: #d8f0e7 !important;
}

.manifest-table-shell ::v-deep tr.manifest-row-returned td {
  background-color: #fdeaea !important;
}

.manifest-table-shell ::v-deep tr.manifest-row-returned:hover td {
  background-color: #fbd8d8 !important;
}

.manifest-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 1.5rem;
  text-align: center;
  background: #fafbfd;
  border: 1px dashed #d8e0ea;
  border-radius: 0.9rem;
}

.manifest-empty-state-large {
  min-height: 220px;
  margin: 0 1rem 1rem;
}

.manifest-loading-state {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.manifest-action-btn {
  border-radius: 0.6rem;
}

.gap-1 {
  gap: 0.75rem;
}

@media (max-width: 991.98px) {
  .manifest-page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .manifest-header-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 767.98px) {
  .manifest-meta-grid {
    grid-template-columns: 1fr;
  }

  .manifest-title {
    font-size: 1.5rem;
  }
}
</style>

<style>
@media print {
  @page {
    size: A2 landscape;
    margin: 0;
  }

  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
  }

  nav.header-navbar,
  .main-menu,
  .header-navbar-shadow,
  footer.footer,
  .customizer-toggle,
  .Vue-Toastification__container,
  .Vue-Toastification__toast,
  .manifest-screen-content {
    display: none !important;
  }

  .content.app-content,
  .app-content,
  .content,
  .content-wrapper,
  .content-body {
    margin: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    background: #ffffff !important;
  }

  .manifest-detail-page {
    padding: 0 !important;
    margin: 0 !important;
    background: #ffffff !important;
  }

  .manifest-print-wrapper {
    display: block !important;
    padding: 10mm 13mm 8mm !important;
    color: #000000 !important;
    font-family: Arial, Helvetica, sans-serif !important;
    background: #ffffff !important;
  }

  .manifest-print-logo {
    display: flex !important;
    justify-content: flex-start !important;
    margin-bottom: 6mm !important;
  }

  .manifest-print-logo img {
    width: 190px !important;
    max-width: 190px !important;
    height: auto !important;
  }

  .manifest-print-title-row {
    display: flex !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    gap: 16px !important;
    margin-bottom: 8mm !important;
  }

  .manifest-print-title-row h1,
  .manifest-print-title-row h2 {
    margin: 0 !important;
    color: #000000 !important;
    font-weight: 800 !important;
  }

  .manifest-print-title-row h1 {
    font-size: 27px !important;
  }

  .manifest-print-title-row h2 {
    font-size: 19px !important;
    padding-top: 5px !important;
  }

  .manifest-print-info-row {
    display: flex !important;
    justify-content: space-between !important;
    gap: 18px !important;
    margin-bottom: 8mm !important;
  }

  .manifest-print-info-card {
    flex: 1 1 0 !important;
    min-height: 88px !important;
    padding: 11px 13px !important;
    border: 2px solid #000000 !important;
    background: #ffffff !important;
    color: #000000 !important;
  }

  .manifest-print-info-line {
    margin-bottom: 4px !important;
    color: #000000 !important;
    font-size: 18px !important;
    line-height: 1.15 !important;
    font-weight: 700 !important;
  }

  .manifest-print-info-line:last-child {
    margin-bottom: 0 !important;
  }

  .manifest-print-info-line strong {
    color: #000000 !important;
    font-weight: 900 !important;
  }

  .manifest-print-table {
    width: 100% !important;
    border-collapse: collapse !important;
    color: #000000 !important;
    table-layout: fixed !important;
  }

  .manifest-print-table th,
  .manifest-print-table td {
    border: 1px solid #000000 !important;
    color: #000000 !important;
    vertical-align: middle !important;
    text-align: center !important;
    word-break: break-word !important;
  }

  .manifest-print-table th {
    padding: 7px 8px !important;
    font-size: 13px !important;
    font-weight: 900 !important;
    text-transform: uppercase !important;
    background: #f0f0f4 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .manifest-print-table td {
    padding: 8px 8px !important;
    font-size: 18px !important;
    line-height: 1.2 !important;
    font-weight: 700 !important;
  }

  .manifest-print-table th:nth-child(1),
  .manifest-print-table td:nth-child(1) {
    width: 16% !important;
  }

  .manifest-print-table th:nth-child(2),
  .manifest-print-table td:nth-child(2) {
    width: 27% !important;
  }

  .manifest-print-table th:nth-child(3),
  .manifest-print-table td:nth-child(3) {
    width: 42% !important;
  }

  .manifest-print-table th:nth-child(4),
  .manifest-print-table td:nth-child(4) {
    width: 15% !important;
    white-space: nowrap !important;
  }

  .manifest-print-table td span,
  .manifest-print-table td strong {
    display: block !important;
    color: #000000 !important;
  }

  .manifest-print-footer {
    display: flex !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    gap: 16px !important;
    margin-top: 8mm !important;
  }

  .manifest-print-footer h2,
  .manifest-print-footer h3 {
    margin: 0 !important;
    color: #000000 !important;
    font-size: 32px !important;
    font-weight: 900 !important;
  }
}
</style>
